import React, { useState, useEffect } from 'react';
import { X, Truck, Package, FileText, Save, Plus, Edit2, Trash2, Search, CheckCircle, AlertCircle, Loader, HelpCircle } from 'lucide-react';
import apiClient from '../services/api';

const VIOLET     = '#667eea';
const VIOLET_DK  = '#764ba2';

const DRSTransactionEntry = ({ onClose, onSave, editData = null }) => {

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const p = JSON.parse(s);
      return p?.session || p;
    } catch { return null; }
  };
  const session   = getSessionData();
  const companyId = session?.companyid;

  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [employees,      setEmployees]      = useState([]);
  const [vehicles,       setVehicles]       = useState([]);
  const [generatingNo,   setGeneratingNo]   = useState(false);
  const [editingIndex,   setEditingIndex]   = useState(null);
  const [showHelp,       setShowHelp]       = useState(false);

  const [awbInput,       setAwbInput]       = useState('');
  const [awbLookupState, setAwbLookupState] = useState('idle');
  const [awbSuggestions, setAwbSuggestions] = useState([]);
  const [awbError,       setAwbError]       = useState('');

  const paymentModes = [
    { modecode:'CSH', modename:'Cash' },
    { modecode:'CHQ', modename:'Cheque' },
    { modecode:'CRD', modename:'Card' },
    { modecode:'BT',  modename:'Bank Transfer' },
    { modecode:'CR',  modename:'Credit' },
  ];
  const deliveryStatuses = [
    { statuscode:'DL', statusname:'Delivered' },
    { statuscode:'PD', statusname:'Pending' },
    { statuscode:'RT', statusname:'Returned' },
    { statuscode:'CN', statusname:'Cancelled' },
    { statuscode:'HD', statusname:'On Hold' },
  ];

  const [formData, setFormData] = useState({
    drsid: null, drsno: '',
    drsdate:      new Date().toISOString().split('T')[0],
    despatchdate: new Date().toISOString().split('T')[0],
    deliveredby_empid: '', checkedby_empid: '', vehicleid: '',
    totalamount: 0, remarks: '', details: [],
  });

  const emptyDetail = () => ({
    drsdetailid: null, consignmentid: 0, awbno: '',
    shipper: '', consignee: '', origin: '', destination: '', weight: '', pieces: '',
    couriercharge: 0, materialcost: 0,
    cc_received: 0, cc_paymentmode: '', cc_bankname: '', cc_chequeno: '', cc_chequedate: '',
    mc_received: 0, mc_paymentmode: '', mc_bankname: '', mc_chequeno: '', mc_chequedate: '',
    collectedby_empid: '', receivername: '',
    receivedtime: new Date().toISOString().slice(0, 16),
    deliverystatus: 'PD', remarks: '',
  });

  const [detailData, setDetailData] = useState(emptyDetail());
  const editDataRef = React.useRef(editData);

  useEffect(() => {
    const init = async () => {
      await loadDropdowns();
      if (editDataRef.current?.drsid) {
        await loadDRS(editDataRef.current.drsid);
      } else {
        setGeneratingNo(true);
        await generateDRSNumber();
        setGeneratingNo(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDropdowns = async () => {
    try {
      const [empRes, vehRes] = await Promise.all([
        apiClient.get('/Employees.php').catch(() => ({ data:{ success:false } })),
        apiClient.get('/Vehicles.php').catch(()  => ({ data:{ success:false } })),
      ]);
      if (empRes.data.success) setEmployees(empRes.data.data || []);
      if (vehRes.data.success) setVehicles(vehRes.data.data  || []);
    } catch (err) { console.error('Dropdown error:', err); }
  };

  const loadDRS = async (id) => {
    try {
      const res = await apiClient.get(`/Drs.php?id=${id}`);
      if (res.data.success) {
        const d = res.data.data;
        setFormData({ drsid:d.drsid, drsno:d.drsno, drsdate:d.drsdate, despatchdate:d.despatchdate, deliveredby_empid:d.deliveredby_empid||'', checkedby_empid:d.checkedby_empid||'', vehicleid:d.vehicleid||'', totalamount:parseFloat(d.totalamount)||0, remarks:d.remarks||'', details:d.details||[] });
      }
    } catch (err) { setError('Failed to load DRS: ' + err.message); }
  };

  const generateDRSNumber = async () => {
    try {
      const res = await apiClient.get('/Drs.php?action=next-number');
      if (res.data.success && res.data.data?.drsno) setFormData(prev => ({ ...prev, drsno: res.data.data.drsno }));
    } catch { setFormData(prev => ({ ...prev, drsno: `DRS-${Date.now()}` })); }
  };

  const handleAwbLookup = async () => {
    const awb = awbInput.trim().toUpperCase();
    if (!awb) { setAwbError('Enter an AWB / Consignment No'); return; }
    const alreadyAdded = formData.details.some((d, idx) => d.awbno === awb && idx !== editingIndex);
    if (alreadyAdded) { setAwbError('This AWB is already added to the DRS'); setAwbLookupState('notfound'); return; }
    setAwbError(''); setAwbLookupState('loading'); setAwbSuggestions([]);
    try {
      const res = await apiClient.get(`/ConsignmentsD.php?awb=${encodeURIComponent(awb)}`);
      if (res.data.success && res.data.data) {
        const c = res.data.data;
        setDetailData(prev => ({ ...prev, consignmentid:c.ConsignmentID||0, awbno:c.ConsignmentNo||awb, shipper:c.Shipper||c.ShipperName||'', consignee:c.Consignee||c.ConsigneeName||'', origin:c.OriginCity||'', destination:c.DestinationCity||'', weight:c.Weight||'', pieces:c.Pieces||'', receivername:prev.receivername||c.Consignee||c.ConsigneeName||'' }));
        setAwbLookupState('found');
      } else { setAwbLookupState('notfound'); setAwbError('No domestic consignment found for this AWB'); }
    } catch (err) { setAwbLookupState('notfound'); setAwbError(err.response?.data?.message||'Lookup failed'); }
  };

  const handleAwbInputChange = async (val) => {
    const v = val.toUpperCase();
    setAwbInput(v); setAwbLookupState('idle'); setAwbError('');
    setDetailData(prev => ({ ...emptyDetail(), couriercharge:prev.couriercharge, materialcost:prev.materialcost, cc_received:prev.cc_received, cc_paymentmode:prev.cc_paymentmode, mc_received:prev.mc_received, mc_paymentmode:prev.mc_paymentmode, deliverystatus:prev.deliverystatus, collectedby_empid:prev.collectedby_empid, receivedtime:prev.receivedtime }));
    if (v.length >= 3) {
      try {
        const res = await apiClient.get(`/ConsignmentsD.php?search=${encodeURIComponent(v)}`);
        if (res.data.success) setAwbSuggestions((res.data.data?.consignments||[]).slice(0, 6));
      } catch { /* ignore */ }
    } else { setAwbSuggestions([]); }
  };

  const handleSelectSuggestion = (c) => {
    setAwbInput(c.ConsignmentNo); setAwbSuggestions([]);
    setDetailData(prev => ({ ...prev, consignmentid:c.ConsignmentID||0, awbno:c.ConsignmentNo||'', shipper:c.ShipperName||'', consignee:c.ConsigneeName||'', origin:c.OriginCity||'', destination:c.DestinationCity||'', weight:c.Weight||'', pieces:c.Pieces||'', receivername:prev.receivername||c.ConsigneeName||'' }));
    setAwbLookupState('found'); setAwbError('');
  };

  const recalcTotal = (rows) => {
    const total = rows.reduce((s, r) => s + (parseFloat(r.cc_received)||0) + (parseFloat(r.mc_received)||0), 0);
    setFormData(prev => ({ ...prev, totalamount: total }));
  };

  const handleAddDetail = () => {
    const awb = awbInput.trim().toUpperCase() || detailData.awbno;
    if (!awb) { setError('AWB number is required — search and select a domestic consignment'); return; }
    if (awbLookupState !== 'found' && editingIndex === null) { setError('Please search and confirm the AWB before adding'); return; }
    setError('');
    const row = { ...detailData, awbno: awb };
    const updated = editingIndex !== null
      ? formData.details.map((d, i) => i === editingIndex ? row : d)
      : [...formData.details, { ...row, drsdetailid: Date.now() }];
    setFormData(prev => ({ ...prev, details: updated }));
    recalcTotal(updated);
    setDetailData(emptyDetail()); setAwbInput(''); setAwbLookupState('idle'); setAwbError(''); setAwbSuggestions([]); setEditingIndex(null);
  };

  const handleEditDetail = (d, i) => {
    setDetailData({ ...d }); setAwbInput(d.awbno||''); setAwbLookupState('found'); setAwbError(''); setAwbSuggestions([]); setEditingIndex(i);
  };

  const handleDeleteDetail = (i) => {
    if (!window.confirm('Delete this line?')) return;
    const updated = formData.details.filter((_, idx) => idx !== i);
    setFormData(prev => ({ ...prev, details: updated })); recalcTotal(updated);
  };

  const handleClearDetail = () => {
    setDetailData(emptyDetail()); setAwbInput(''); setAwbLookupState('idle'); setAwbError(''); setAwbSuggestions([]); setEditingIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.drsdate)            { setError('DRS date is required');        return; }
    if (!formData.despatchdate)       { setError('Despatch date is required');    return; }
    if (formData.details.length === 0){ setError('Add at least one detail line'); return; }
    setLoading(true); setError('');
    try {
      const dataToSave = { ...formData, companyid: companyId };
      if (editData?.drsid) dataToSave.drsid = editData.drsid;
      await onSave(dataToSave);
    } catch (err) { setError(err.message || 'Failed to save DRS'); }
    finally { setLoading(false); }
  };

  const getPMName = (code) => paymentModes.find(m => m.modecode === code)?.modename || code || '—';
  const getStName = (code) => deliveryStatuses.find(s => s.statuscode === code)?.statusname || code || '—';

  // shared classes
  const inpCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-[0.95rem] bg-white outline-none transition-all duration-150 focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] font-[inherit] box-border';
  const selCls = inpCls + ' cursor-pointer';
  const numInp = inpCls + ' text-right font-mono font-semibold tracking-wide';
  const lblCls = 'block text-sm font-medium text-gray-700 mb-1.5';
  const SecHdr = ({ icon: Icon, children }) => (
    <div className="flex items-center gap-2 text-[1.05rem] font-semibold text-gray-800 mb-4 pb-3 border-b-2 border-gray-200">
      {Icon && <Icon size={18} style={{ color: VIOLET }} />}{children}
    </div>
  );
  const SubLabel = ({ children }) => (
    <div className="text-[0.75rem] font-semibold text-gray-500 mb-2.5 uppercase tracking-widest border-l-4 border-gray-300 pl-2">{children}</div>
  );

  const awbStatusIcon = () => {
    if (awbLookupState==='loading')  return <Loader size={16} className="text-violet-500" style={{ animation:'spin 1s linear infinite' }} />;
    if (awbLookupState==='found')    return <CheckCircle size={16} className="text-emerald-600" />;
    if (awbLookupState==='notfound') return <AlertCircle size={16} className="text-red-600" />;
    return null;
  };

  const spinStyle = { width:18,height:18,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'drs-spin 0.8s linear infinite',display:'inline-block' };

  if (!companyId) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-5">
        <div className="bg-white rounded-xl max-w-[600px] w-full shadow-2xl flex flex-col" style={{ animation:'drs-slide 0.3s ease-out' }}>
          <div className="flex items-center justify-between px-8 py-6 rounded-t-xl border-b-4" style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})`, borderBottomColor:'#5a67d8' }}>
            <h2 className="m-0 text-xl font-bold text-white">Session Required</h2>
            <button onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/20 border-2 border-white/30 cursor-pointer hover:bg-white/30 transition-all duration-150"><X size={20} color="#fff" /></button>
          </div>
          <div className="p-6 text-center text-gray-600">
            <p>Please select a company and financial year from the dashboard.</p>
            <button onClick={onClose} className="mt-4 px-5 py-2.5 text-white border-0 rounded-lg cursor-pointer text-sm font-semibold" style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-5 overflow-y-auto">
      <style>{`
        @keyframes drs-spin  { to { transform:rotate(360deg); } }
        @keyframes drs-slide { from { opacity:0; transform:translateY(-20px); } to { opacity:1; transform:translateY(0); } }
        .drs-sugg:hover { background:#f0f4ff; }
        .drs-detail-tr:hover td { background:#f9fafb; }
      `}</style>
      <div className="bg-white rounded-xl w-full flex flex-col shadow-2xl"
        style={{ maxWidth:1300, maxHeight:'95vh', animation:'drs-slide 0.3s ease-out' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-7 flex-shrink-0 border-b-4 rounded-t-xl"
          style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})`, borderBottomColor:'#5a67d8', boxShadow:'0 4px 12px rgba(0,0,0,0.15)' }}>
          <div className="flex items-center gap-6 flex-1">
            <div className="w-[60px] h-[60px] bg-white/30 rounded-[16px] flex items-center justify-center shadow-lg flex-shrink-0">
              <Truck size={24} color="#fff" />
            </div>
            <div className="flex-1 flex items-center gap-4 flex-wrap">
              <span className="text-[1.8rem] font-bold text-white" style={{ textShadow:'0 2px 4px rgba(0,0,0,0.5)' }}>
                {editData ? 'Edit' : 'New'} Daily Run Sheet (DRS)
              </span>
              <span className="font-mono text-[1rem] font-semibold text-white bg-white/25 px-3.5 py-1.5 rounded-md border border-white/40">
                📋 {generatingNo ? 'Generating...' : (formData.drsno || 'Loading...')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button type="button" onClick={() => setShowHelp(true)} title="How to fill this form"
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/20 border-2 border-white/30 cursor-pointer hover:bg-white/35 transition-all duration-150">
              <HelpCircle size={20} color="#fff" />
            </button>
            <button onClick={onClose}
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/20 border-2 border-white/30 cursor-pointer hover:bg-white/35 transition-all duration-150">
              <X size={20} color="#fff" />
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 mx-8 mt-4 px-4 py-3 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm flex-shrink-0">
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-8 py-6 bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded">

            {/* DRS Header Section */}
            <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
              <SecHdr icon={Truck}>DRS Information</SecHdr>
              <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                <div><label className={lblCls}>DRS Number</label>
                  <input value={generatingNo ? 'Generating...' : formData.drsno} readOnly
                    className={`${inpCls} font-bold text-[1.1rem] ${generatingNo ? 'bg-yellow-50' : 'bg-blue-50'} cursor-not-allowed`} />
                </div>
                <div><label className={lblCls}>DRS Date *</label>
                  <input type="date" value={formData.drsdate} required className={inpCls}
                    onChange={e => setFormData(p => ({ ...p, drsdate: e.target.value }))} />
                </div>
                <div><label className={lblCls}>Despatch Date *</label>
                  <input type="date" value={formData.despatchdate} required className={inpCls}
                    onChange={e => setFormData(p => ({ ...p, despatchdate: e.target.value }))} />
                </div>
                <div><label className={lblCls}>Total Collected</label>
                  <input value={parseFloat(formData.totalamount||0).toFixed(2)} readOnly
                    className={`${numInp} bg-emerald-50 text-emerald-700 font-bold text-[1.1rem] cursor-not-allowed`} />
                </div>
                <div><label className={lblCls}>Delivered By</label>
                  <select value={formData.deliveredby_empid} className={selCls}
                    onChange={e => setFormData(p => ({ ...p, deliveredby_empid: e.target.value }))}>
                    <option value="">-- Select Employee --</option>
                    {employees.map(emp => <option key={emp.employeeid} value={emp.employeeid}>{emp.employeename}</option>)}
                  </select>
                </div>
                <div><label className={lblCls}>Checked By</label>
                  <select value={formData.checkedby_empid} className={selCls}
                    onChange={e => setFormData(p => ({ ...p, checkedby_empid: e.target.value }))}>
                    <option value="">-- Select Employee --</option>
                    {employees.map(emp => <option key={emp.employeeid} value={emp.employeeid}>{emp.employeename}</option>)}
                  </select>
                </div>
                <div><label className={lblCls}>Vehicle</label>
                  <select value={formData.vehicleid} className={selCls}
                    onChange={e => setFormData(p => ({ ...p, vehicleid: e.target.value }))}>
                    <option value="">-- Select Vehicle --</option>
                    {vehicles.map(v => <option key={v.VehicleID} value={v.VehicleID}>{v.RegistrationNo}</option>)}
                  </select>
                </div>
                <div><label className={lblCls}>Remarks</label>
                  <input type="text" value={formData.remarks} className={inpCls}
                    onChange={e => setFormData(p => ({ ...p, remarks: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* Detail Entry */}
            <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
              <SecHdr icon={Package}>{editingIndex !== null ? 'Edit Detail Line' : 'Add Detail Line'}</SecHdr>

              {/* Step 1: AWB */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3.5 mb-3">
                <SubLabel>1. Search Domestic Consignment (AWB)</SubLabel>
                <div className="flex gap-2 items-start relative">
                  <div className="flex-1 relative">
                    <input type="text" value={awbInput}
                      onChange={e => handleAwbInputChange(e.target.value)}
                      onKeyDown={e => { if (e.key==='Enter') { e.preventDefault(); handleAwbLookup(); } }}
                      placeholder="Type AWB / CNS-D-000001 and press Search or Enter…"
                      className="w-full pr-10 pl-3 py-2.5 border-2 rounded-md text-[0.95rem] font-mono font-semibold outline-none transition-all duration-150 box-border"
                      style={{
                        borderColor: awbLookupState==='found'?'#16a34a':awbLookupState==='notfound'?'#dc2626':'#d1d5db',
                        background:  awbLookupState==='found'?'#f0fdf4':'#fff',
                      }} />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">{awbStatusIcon()}</div>

                    {/* Suggestions */}
                    {awbSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-xl z-[100] max-h-[220px] overflow-y-auto">
                        {awbSuggestions.map(c => (
                          <div key={c.ConsignmentID} onClick={() => handleSelectSuggestion(c)}
                            className="drs-sugg flex justify-between items-center px-3 py-2.5 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-75">
                            <span className="font-mono font-bold text-[0.9rem]" style={{ color: VIOLET }}>{c.ConsignmentNo}</span>
                            <span className="text-[0.82rem] text-gray-500 ml-3 text-right">
                              {c.ShipperName} → {c.ConsigneeName}
                              {c.OriginCity && c.DestinationCity && <span className="text-gray-400 ml-1.5">({c.OriginCity} → {c.DestinationCity})</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={handleAwbLookup} disabled={awbLookupState==='loading'}
                    className="flex items-center gap-1.5 px-4 py-2.5 border-0 rounded-md text-white font-semibold text-[0.9rem] cursor-pointer whitespace-nowrap transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
                    <Search size={16} />{awbLookupState==='loading' ? 'Searching…' : 'Search'}
                  </button>
                </div>
                {awbError && (
                  <div className="flex items-center gap-1 mt-1.5 text-[0.82rem] text-red-600">
                    <AlertCircle size={13} /> {awbError}
                  </div>
                )}
                {awbLookupState==='found' && detailData.consignmentid > 0 && (
                  <div className="mt-2.5 px-3.5 py-2.5 bg-emerald-50 border border-emerald-300 rounded-md grid gap-1.5 text-[0.82rem]"
                    style={{ gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))' }}>
                    {[['Shipper',detailData.shipper],['Consignee',detailData.consignee],['From',detailData.origin],['To',detailData.destination],['Weight',detailData.weight?`${detailData.weight} kg`:''],['Pieces',detailData.pieces||'']].filter(([,v])=>v).map(([l,v])=>(
                      <div key={l}><span className="text-gray-500 font-semibold">{l}: </span><span className="text-emerald-800 font-medium">{v}</span></div>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 2: Delivery Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3.5 mb-3">
                <SubLabel>2. Delivery Info</SubLabel>
                <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                  <div><label className={lblCls}>Receiver Name</label><input type="text" value={detailData.receivername} className={inpCls} onChange={e => setDetailData(p=>({...p,receivername:e.target.value}))} /></div>
                  <div><label className={lblCls}>Status</label>
                    <select value={detailData.deliverystatus} className={selCls} onChange={e => setDetailData(p=>({...p,deliverystatus:e.target.value}))}>
                      {deliveryStatuses.map(s => <option key={s.statuscode} value={s.statuscode}>{s.statusname}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Delivery Time</label><input type="datetime-local" value={detailData.receivedtime} className={inpCls} onChange={e => setDetailData(p=>({...p,receivedtime:e.target.value}))} /></div>
                </div>
              </div>

              {/* Step 3: Courier Charge */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3.5 mb-3">
                <SubLabel>3. Courier Charge</SubLabel>
                <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                  <div><label className={lblCls}>Amount</label><input type="number" step="0.01" value={detailData.couriercharge} className={numInp} onChange={e => setDetailData(p=>({...p,couriercharge:e.target.value}))} /></div>
                  <div><label className={lblCls}>Received</label><input type="number" step="0.01" value={detailData.cc_received} className={`${numInp} text-emerald-700`} onChange={e => setDetailData(p=>({...p,cc_received:e.target.value}))} /></div>
                  <div><label className={lblCls}>Mode</label>
                    <select value={detailData.cc_paymentmode} className={selCls} onChange={e => setDetailData(p=>({...p,cc_paymentmode:e.target.value}))}>
                      <option value="">Select</option>
                      {paymentModes.map(m => <option key={m.modecode} value={m.modecode}>{m.modename}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Collected By</label>
                    <select value={detailData.collectedby_empid} className={selCls} onChange={e => setDetailData(p=>({...p,collectedby_empid:e.target.value}))}>
                      <option value="">Select</option>
                      {employees.map(emp => <option key={emp.employeeid} value={emp.employeeid}>{emp.employeename}</option>)}
                    </select>
                  </div>
                </div>
                {detailData.cc_paymentmode==='CHQ' && (
                  <div className="grid gap-4 mt-2" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                    <div><label className={lblCls}>Bank</label><input value={detailData.cc_bankname} className={inpCls} onChange={e=>setDetailData(p=>({...p,cc_bankname:e.target.value}))} /></div>
                    <div><label className={lblCls}>Cheque No</label><input value={detailData.cc_chequeno} className={inpCls} onChange={e=>setDetailData(p=>({...p,cc_chequeno:e.target.value}))} /></div>
                    <div><label className={lblCls}>Cheque Date</label><input type="date" value={detailData.cc_chequedate} className={inpCls} onChange={e=>setDetailData(p=>({...p,cc_chequedate:e.target.value}))} /></div>
                  </div>
                )}
              </div>

              {/* Step 4: Material Cost */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3.5 mb-3">
                <SubLabel>4. Material Cost</SubLabel>
                <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                  <div><label className={lblCls}>Amount</label><input type="number" step="0.01" value={detailData.materialcost} className={numInp} onChange={e=>setDetailData(p=>({...p,materialcost:e.target.value}))} /></div>
                  <div><label className={lblCls}>Received</label><input type="number" step="0.01" value={detailData.mc_received} className={`${numInp} text-emerald-700`} onChange={e=>setDetailData(p=>({...p,mc_received:e.target.value}))} /></div>
                  <div><label className={lblCls}>Mode</label>
                    <select value={detailData.mc_paymentmode} className={selCls} onChange={e=>setDetailData(p=>({...p,mc_paymentmode:e.target.value}))}>
                      <option value="">Select</option>
                      {paymentModes.map(m => <option key={m.modecode} value={m.modecode}>{m.modename}</option>)}
                    </select>
                  </div>
                </div>
                {detailData.mc_paymentmode==='CHQ' && (
                  <div className="grid gap-4 mt-2" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                    <div><label className={lblCls}>Bank</label><input value={detailData.mc_bankname} className={inpCls} onChange={e=>setDetailData(p=>({...p,mc_bankname:e.target.value}))} /></div>
                    <div><label className={lblCls}>Cheque No</label><input value={detailData.mc_chequeno} className={inpCls} onChange={e=>setDetailData(p=>({...p,mc_chequeno:e.target.value}))} /></div>
                    <div><label className={lblCls}>Cheque Date</label><input type="date" value={detailData.mc_chequedate} className={inpCls} onChange={e=>setDetailData(p=>({...p,mc_chequedate:e.target.value}))} /></div>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className={lblCls}>Line Remarks</label>
                <input type="text" value={detailData.remarks} className={inpCls} onChange={e=>setDetailData(p=>({...p,remarks:e.target.value}))} />
              </div>

              <div className="flex gap-2 justify-end mt-2">
                <button type="button" onClick={handleClearDetail}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-gray-600 border-2 border-gray-200 rounded-lg text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all duration-150">
                  <X size={16} /> Clear
                </button>
                <button type="button" onClick={handleAddDetail}
                  title={awbLookupState!=='found'&&editingIndex===null?'Search and confirm an AWB first':''}
                  className="flex items-center gap-2 px-4 py-2 text-white border-0 rounded-lg text-sm font-semibold transition-all duration-150"
                  style={{ background:(awbLookupState==='found'||editingIndex!==null)?`linear-gradient(135deg,${VIOLET},${VIOLET_DK})`:'#9ca3af', cursor:(awbLookupState==='found'||editingIndex!==null)?'pointer':'not-allowed' }}>
                  {editingIndex!==null ? <><Edit2 size={16}/> Update Line</> : <><Plus size={16}/> Add Line</>}
                </button>
              </div>
            </div>

            {/* Details Table */}
            <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
              <SecHdr icon={FileText}>DRS Details ({formData.details.length})</SecHdr>
              {formData.details.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Package size={40} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No detail lines added yet. Search a domestic consignment AWB above to begin.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full border-collapse text-[0.9rem]">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        {['#','AWB','Shipper','Consignee','CC Amt','MC Amt','CC Rcvd','MC Rcvd','Total','Mode','Status','Actions'].map(h => (
                          <th key={h} className="px-3 py-2.5 text-left text-[0.85rem] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {formData.details.map((d, i) => (
                        <tr key={i} className={`drs-detail-tr border-b border-gray-200 last:border-b-0 ${editingIndex===i?'bg-yellow-50':''}`}>
                          <td className="px-3 py-3 text-gray-400 text-center">{i+1}</td>
                          <td className="px-3 py-3 font-mono font-bold" style={{ color: VIOLET }}>{d.awbno}</td>
                          <td className="px-3 py-3 text-[0.82rem]">{d.shipper||'—'}</td>
                          <td className="px-3 py-3 text-[0.82rem]">{d.consignee||d.receivername||'—'}</td>
                          <td className="px-3 py-3 text-right font-mono">{parseFloat(d.couriercharge||0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-right font-mono">{parseFloat(d.materialcost||0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-right font-mono font-semibold text-emerald-600">{parseFloat(d.cc_received||0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-right font-mono font-semibold text-emerald-600">{parseFloat(d.mc_received||0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-right font-mono font-bold" style={{ color: VIOLET }}>{(parseFloat(d.cc_received||0)+parseFloat(d.mc_received||0)).toFixed(2)}</td>
                          <td className="px-3 py-3 text-[11px]">
                            {d.cc_paymentmode && <span className="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-bold mr-1 bg-blue-500">CC:{getPMName(d.cc_paymentmode)}</span>}
                            {d.mc_paymentmode && <span className="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-bold bg-emerald-500">MC:{getPMName(d.mc_paymentmode)}</span>}
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${d.deliverystatus==='DL'?'bg-emerald-100 text-emerald-800':'bg-gray-100 text-gray-600'}`}>
                              {getStName(d.deliverystatus)}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            <button type="button" onClick={() => handleEditDetail(d,i)}
                              className="inline-flex items-center justify-center w-7 h-7 rounded border border-blue-200 text-blue-500 bg-transparent cursor-pointer hover:bg-blue-50 hover:border-blue-500 transition-all duration-100 ml-1"><Edit2 size={14} /></button>
                            <button type="button" onClick={() => handleDeleteDetail(i)}
                              className="inline-flex items-center justify-center w-7 h-7 rounded border border-red-200 text-red-600 bg-transparent cursor-pointer hover:bg-red-50 hover:border-red-600 transition-all duration-100 ml-1"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                        <td colSpan={8} className="px-3 py-3 text-right text-gray-600 font-semibold">Grand Total:</td>
                        <td className="px-3 py-3 text-right font-mono text-[15px]" style={{ color: VIOLET }}>{parseFloat(formData.totalamount||0).toFixed(2)}</td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-8 py-5 bg-gray-50 border-t border-gray-200 rounded-b-xl flex-shrink-0">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-white text-gray-500 border-2 border-gray-200 rounded-lg text-sm font-semibold cursor-pointer hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150">
              Cancel
            </button>
            <button type="submit" disabled={loading || generatingNo}
              className="flex items-center gap-2 px-6 py-3 text-white border-0 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 hover:-translate-y-0.5 hover:shadow-lg"
              style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
              {loading ? <><span style={spinStyle} />Saving...</> : <><Save size={18} />Save DRS</>}
            </button>
          </div>
        </form>
      </div>

      {showHelp && (
        <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl w-full max-w-[640px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
              style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
              <h3 className="flex items-center gap-2 text-lg font-semibold m-0" style={{ color: '#fff' }}>
                <HelpCircle size={18} /> How to Fill This Form
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150"
                style={{ color: '#fff' }}>
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {[
                { title: 'DRS Information', desc: 'The DRS Number is auto-generated. Set the DRS Date, Despatch Date, and optionally assign the driver ("Delivered By"), a checker, and a vehicle.' },
                { title: 'Search a Consignment', desc: 'In "Add Detail Line", type an AWB / consignment number and press Search (or Enter). Matching suggestions appear as you type — click one to select it directly.' },
                { title: 'Delivery Info', desc: 'Enter the receiver name, delivery status (Delivered, Pending, Returned, etc.), and delivery time for this line.' },
                { title: 'Courier Charge & Material Cost', desc: 'Enter the charge amount and how much was actually received, with payment mode. If "Cheque" is selected, bank, cheque number, and date fields appear.' },
                { title: 'Add the Line', desc: 'Click "Add Line" to add it to the DRS Details table below. Repeat for each consignment on this run. Use the Edit/Delete icons in the table to change lines.' },
                { title: 'Save', desc: 'Click "Save DRS" once all lines are added. The Total Collected and Grand Total update automatically as you add lines.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DRSTransactionEntry;